import { useEffect, useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  Typography
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import { useContextState } from '../../hooks/theme'
import { Column } from './types'
import useStyles from './styles'

const FIRST_COL_WIDTH_DIVISION = 260
const FIRST_COL_WIDTH_ZONE = 231
const FIRST_COL_WIDTH_UNIT = 245
const VALUE_COL_WIDTH = 60
const TOTAL_COL_WIDTH = 140
const PROM_COL_WIDTH = 140

const normalizeColumns = (columns: Column[], firstColWidth: number) =>
  columns.map((col, index) => {
    if (index === 0) return { ...col, width: firstColWidth }
    if (col.field === 'total') return { ...col, width: TOTAL_COL_WIDTH }
    if (col.field === 'promedio') return { ...col, width: PROM_COL_WIDTH }
    return { ...col, width: VALUE_COL_WIDTH }
  })

interface CollapsibleTablePropsCustom {
  columns: { columns: Column[]; data: any[] }
  sx?: any
  customHeaderGradient?: boolean
  firstTabSelected?: boolean
  validationToExpand: boolean
}

const CollapsibleTable = ({ columns, sx, customHeaderGradient = false, firstTabSelected = false, validationToExpand }: CollapsibleTablePropsCustom) => {
  const { colors } = useContextState()
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [allExpanded, setAllExpanded] = useState(validationToExpand)

  useEffect(()=>{
    if (!validationToExpand) {
      setExpandedRows(new Set())
      setAllExpanded(false)
    } else {
      const allIds = new Set(collectAllRowIds(data, 'root', 0))
      setExpandedRows(allIds)
      setAllExpanded(true)
    }
  },[validationToExpand])

  const collectAllRowIds = (rows: any[], idPrefix: string, level: number): string[] => {
    let ids: string[] = []
    rows.forEach((row, idx) => {
      const id = `${idPrefix}-${idx}`
      ids.push(id)
      if (row.subRows && row.subRows.length > 0) {
        ids = ids.concat(collectAllRowIds(row.subRows, id, level + 1))
      }
    })
    return ids
  }

  const handleToggleAll = () => {
    if (allExpanded) {
      setExpandedRows(new Set())
      setAllExpanded(false)
    } else {
      const allIds = new Set(collectAllRowIds(data, 'root', 0))
      setExpandedRows(allIds)
      setAllExpanded(true)
    }
  }
  const styles = useStyles()

  const getFirstColWidth = (level: number) => {
    if (level === 0) return FIRST_COL_WIDTH_UNIT
    if (level === 1) return FIRST_COL_WIDTH_ZONE
    return FIRST_COL_WIDTH_UNIT
  }
  const data = columns.data
  const toggleRow = (id: string) => {
    const next = new Set(expandedRows)
    next.has(id) ? next.delete(id) : next.add(id)
    setExpandedRows(next)
  }


  const getBackgroundColor = (value: any) => {
    const numValue = parseFloat(value)
    if (value === null || value === undefined || value === '' || value === '-' || isNaN(numValue)) return colors.color10
    if (numValue <= 60) return colors.color12
    if (numValue >= 61 && numValue <= 80) return colors.orangeMedium
    if (numValue > 80) return colors.color8
    return 'transparent'
  }

  const renderValueCell = (value: any, field?: string) => {

    if ((value === '-' || value === null || value === undefined || value === '') && field === 'total') {
      return <Box sx={{ padding: '16px 3px', minHeight: '28px', textAlign: 'center' }}>0,0%</Box>;
    }
    const num = parseFloat(value)
    if ((value === '-' || value === null || value === undefined || value === '') && field !== 'total') {
      return <Box sx={{ padding: '16px 3px', minHeight: '28px', textAlign: 'center', backgroundColor: colors.color39, borderRadius: '4px' }}>0,0%</Box>;
    }
    if (isNaN(num)) return value ?? '-'
    const isTotal = field === 'total'
    return (
      <Box
        sx={{
          backgroundColor: isTotal ? 'transparent' : getBackgroundColor(num),
          ...styles.cells,
        }}
      >
        {num/* .toFixed(1) */}%
      </Box>
    )
  }


  const renderRow = (
    row: any,
    level: number,
    idPrefix: string,
    rowIndex: number
  ) => {
    const ID = `${idPrefix}-${rowIndex}`
    const hasChildren = row.subRows && row.subRows.length > 0
    const expanded = expandedRows.has(ID)
    const backgroundColor = rowIndex % 2 === 0 ? colors.evenTable : colors.oddTable
    const firstColWidth = getFirstColWidth(level)
    const normalizedColumnsLevel = normalizeColumns(columns.columns, firstColWidth)

    return (
      <>
        <TableRow hover sx={{ backgroundColor, '& td, & th': { borderBottom: 'none' } }}>
          {normalizedColumnsLevel.map((column, colIndex) => (
            <TableCell
              key={column.field}
              sx={{
                width: column.width,
                maxWidth: column.width,
                minWidth: column.width,
                padding: '8px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: colIndex === 0 ? 'normal' : 'nowrap',
              }}
            >
              {colIndex === 0 ? (
                <Box
                  sx={styles.colInitial}
                >
                  {hasChildren ? (
                    <IconButton size="small" onClick={() => toggleRow(ID)}>
                      {expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                    </IconButton>
                  ) : (
                    <IconButton size="small" sx={{ mr: 1, visibility: 'hidden' }}>
                      <ArrowDropDownIcon />
                    </IconButton>
                  )}
                  <Typography
                    sx={styles.titles}
                  >
                    {row[column.field]}
                  </Typography>
                </Box>
              ) : (
                renderValueCell(row[column.field], column.field)
              )}
            </TableCell>
          ))}
        </TableRow>

        {hasChildren && (
          <TableRow>
            <TableCell colSpan={normalizedColumnsLevel.length} sx={{ p: 0 }}>
              <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ backgroundColor: colors.color44 }}>
                <Box sx={styles.containerCollapse}>
                  <Box sx={styles.secondaryCollapse}>
                    {row.subTitle && (
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 'bold',
                          color: colors.color9,
                          mt: 1,
                          fontSize: '1rem',
                          pl: `${((level + 1) * 8) + 34}px`,
                          py: '12px',
                          backgroundColor: colors.color5,
                          borderTopLeftRadius: '8px',
                        }}
                      >
                        {row.subTitle}
                      </Typography>
                    )}
                    <Table size="small" sx={styles.secondaryTable}>
                      <colgroup>
                        {normalizedColumnsLevel.map((c) => (
                          <col key={c.field} style={{ width: c.width }} />
                        ))}
                      </colgroup>
                      <TableBody>
                        {row.subRows.map((child: any, idx: number) =>
                          renderRow(child, level + 1, ID, idx)
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  const normalizedColumnsHeader = normalizeColumns(columns.columns, FIRST_COL_WIDTH_DIVISION);
  return (
    <TableContainer component={Paper} sx={{
      ...sx,
      maxHeight: '600px',
      overflowY: 'auto',
      scrollbarWidth: 'none', // Firefox
      '&::-webkit-scrollbar': { display: 'none' }, // Chrome/Safari
    }}>
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          {normalizedColumnsHeader.map((c) => (
            <col key={c.field} style={{ width: `${c.width}px`, minWidth: `${c.width}px`, maxWidth: `${c.width}px` }} />
          ))}
        </colgroup>

        <TableHead>
          <TableRow
            sx={{
              ...(customHeaderGradient && {
                backgroundImage: `linear-gradient(to right, ${colors.contrastEndpoint}, ${colors.primaryEndpoint}, ${colors.secondaryEndpoint})`,
                borderRadius: firstTabSelected ? '0 8px 0 0' : '8px 8px 0 0',
                position: 'sticky',
                top: 0,
                zIndex: 100,
              })
            }}
          >
            {normalizedColumnsHeader.map((column, colIndex) => (
              <TableCell
                key={column.field}
                sx={{
                  width: column.width,
                  maxWidth: column.width,
                  minWidth: column.width,
                  fontWeight: 'bold',
                  backgroundColor: customHeaderGradient ? 'transparent' : '#f5f5f5',
                  color: customHeaderGradient ? colors.color5 : undefined,
                  padding: '8px',
                  overflow: 'hidden',
                  py: '16px',
                  ...(colIndex === 0 ? { pl: '8px' } : {})
                }}
                align={column.align || 'left'}
              >
                {colIndex === 0 ? (
                  <IconButton size="small" onClick={handleToggleAll} sx={{ mr: 1, color: colors.color5 }}>
                    {allExpanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                  </IconButton>
                ) : null}
                {column.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.columns.length} align="center" sx={{ fontSize: '18px', color: colors.color13 }}>
                No hay información
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, index) => renderRow(row, 0, 'root', index))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default CollapsibleTable
